import React, { useState } from 'react';
import styled from 'styled-components';
import { Badge, Btn, Failed, Grid, IconBtn, Icons, LinkBtn, Loading, Note, T } from './ui';

/**
 * Section 2 — the template catalog, as the card grid in the dashboard design.
 *
 * `campaignEligible` decides what is selectable when composing. Only the campaign template is: the
 * other eight are sent in response to an account or billing event, and offering them here would put
 * "Password Reset to 12,482 fans" one click away.
 *
 * The eight are still shown with their card and their preview links — an operator needs to read
 * what the system sends on their behalf — but the reason they cannot be composed is stated on the
 * card rather than left to be discovered.
 */

/**
 * Card names, blurbs and badge words, taken verbatim from the Figma board.
 *
 * Copied rather than paraphrased, and held here because the API returns none of it: `listTemplates`
 * returns the key, category, delivery class and version — the facts that govern behaviour. A blurb
 * is editorial, so it lives with the screen that shows it rather than being added to a model with
 * no other use for it.
 *
 * `badge` is the design's own word and `category` below is the enum the backend stores. Where they
 * differ the design's word is shown, because it is the vocabulary the client approved and reads
 * better on a card — but the difference is not cosmetic and the note at the foot of the section
 * spells it out: the *category* is what an unsubscribe silences, so an operator who reads "ALERT"
 * and assumes it always sends needs somewhere to find out otherwise.
 */
export const CARDS = {
  welcome: {
    name: 'Welcome Email',
    badge: 'Onboarding',
    blurb:
      'First impression template welcoming new Playaz to the arena. Introduces premium membership perks and team alignment setups.'
  },
  campaign: {
    name: 'Campaign Email',
    badge: 'Marketing',
    blurb:
      'Rich editorial design optimized for seasonal drops, dynamic event triggers, and specialized team fan alerts.'
  },
  emailVerification: {
    name: 'Email Verification',
    badge: 'System',
    blurb:
      'Minimal transactional template delivering secure, single-use activation pins and quick-verify CTA links.'
  },
  passwordReset: {
    name: 'Password Reset',
    badge: 'Security',
    blurb:
      'Automated high-security format delivering reset links with expiration timestamps and device details.'
  },
  passwordChanged: {
    name: 'Password Changed',
    badge: 'Security',
    blurb:
      "Standard security confirmation dispatching notification that a user profile's password has been successfully updated."
  },
  loginAlert: {
    name: 'New Device/Login Alert',
    badge: 'Alert',
    blurb:
      'Immediate warning template reporting unauthorized logins, containing location, IP address, and browser data.'
  },
  subscriptionConfirmation: {
    name: 'Subscription Confirmation',
    badge: 'Billing',
    blurb:
      'Thank-you notification validating billing milestones, active upgrades (MVP Premium), and invoice attachments.'
  },
  paymentFailed: {
    name: 'Payment Failed',
    badge: 'Alert',
    blurb:
      'Polite warning trigger encouraging users to update expired payment cards to keep active MVP status.'
  },
  billingNotice: {
    name: 'Billing Notice',
    badge: 'Billing',
    blurb:
      'Automated monthly ledger summarizing active statements, credit limits, and historical community transactions.'
  }
};

/** The order the design lays the nine cards out in, reading across. */
const ORDER = [
  'welcome',
  'campaign',
  'emailVerification',
  'passwordReset',
  'passwordChanged',
  'loginAlert',
  'subscriptionConfirmation',
  'paymentFailed',
  'billingNotice'
];

/**
 * The catalog grid.
 *
 * `Grid` sizes each row to its own tallest card, which lines a row up but still leaves the second
 * row a different height from the first — nine boxes in three sizes. `grid-auto-rows: 1fr` makes
 * every row the height of the tallest card in the whole catalog, so every box is the same box.
 *
 * Only while there is more than one column. Below 1100px `Grid` collapses to a single column, and
 * a uniform height there would pad every card to the tallest one's blurb with nothing beside it to
 * line up against — whitespace bought for an alignment nobody can see.
 */
const CatalogGrid = styled(Grid)`
  grid-auto-rows: 1fr;

  @media (max-width: 1100px) {
    grid-auto-rows: auto;
  }
`;

const TemplateCard = styled.article`
  border: 1px solid ${(props) => (props.$selected ? T.primary : T.border)};
  box-shadow: ${(props) => (props.$selected ? `0 0 0 3px ${T.primarySoft}` : 'none')};
  border-radius: 13px;
  background: ${T.card};
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 11px;

  /*
    Every card in a row is the height of the tallest one.

    The grid these sit in sets \`align-items: start\`, which is right for panels of unrelated
    content and wrong for a catalog: each card then took the height of its own blurb, so a row of
    them ended at three different heights — 287, 306 and 266 pixels at a 1180px viewport — and the
    divider, the buttons and the two preview links sat at a different height on every card.

    \`align-self\` rather than changing \`Grid\`: the alignment is a fact about a catalog of
    like-for-like cards, not about every grid in the Email Manager, and \`Grid\` also lays out the
    analytics tiles and the scheduler's two columns.

    This is also what \`Blurb\`'s \`flex: 1\` was always written for. A card that is not stretched
    has no slack for the blurb to absorb, so the rule did nothing; stretched, it puts the slack
    above the divider, which is the one place in the card where blank space says nothing.
  */
  align-self: stretch;
`;

const CardName = styled.h3`
  margin: 0;
  font-size: 16.5px;
  font-weight: 700;
  color: ${T.heading};
`;

const Blurb = styled.p`
  margin: 0;
  flex: 1;
  font-size: 13px;
  line-height: 1.6;
  color: ${T.muted};
`;

const Divider = styled.div`
  height: 1px;
  background: ${T.rowBorder};
  margin: 3px 0;
`;

const TemplateList = ({
  templates = [],
  selected,
  onSelect,
  onPreview,
  loading,
  failed,
  onRetry
}) => {
  const [copied, setCopied] = useState(null);

  /*
    Says what state it is in, rather than rendering an empty grid.

    `templates` starts as `[]`, and the grid rendered with no cards and no empty state during the
    fetch — a blank page. The shell's seeding notice was gated on the fetch having finished, so the
    gap in between said nothing at all.

    Below the hook, not above it: an early return before `useState` changes the hook order between
    renders, which React forbids and eslint caught.
  */
  const byKey = new Map(templates.map((template) => [template.key, template]));
  const ordered = [
    ...ORDER.filter((key) => byKey.has(key)),
    ...templates.map((template) => template.key).filter((key) => !ORDER.includes(key))
  ];

  /**
   * The copy button beside each Edit Template.
   *
   * The design gives no label for it. It copies the template key, which is the one thing on a card
   * worth putting on a clipboard — it is what an engineer asks for and what every endpoint takes.
   * Duplicating a template would be the other reading, and there is no endpoint for that.
   */
  const copyKey = async (key) => {
    try {
      await navigator.clipboard?.writeText(key);
      setCopied(key);
      setTimeout(() => setCopied(null), 1600);
    } catch {
      // A clipboard the browser refuses is not worth an error toast on a convenience button.
    }
  };

  const mismatched = ordered.filter((key) => {
    const card = CARDS[key];
    return card && byKey.get(key)?.category !== card.badge.toLowerCase();
  });

  if (failed) {
    return (
      <Failed title="The template catalog could not be loaded" onRetry={onRetry}>
        This is not an empty catalog — the request itself failed.
      </Failed>
    );
  }

  if (loading && templates.length === 0) {
    return <Loading title="Loading templates…" />;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <CatalogGrid cols="repeat(auto-fit, minmax(320px, 1fr))">
        {ordered.map((key) => {
          const template = byKey.get(key);
          const card = CARDS[key] || { name: key, badge: template?.category || '—', blurb: '' };
          const composable = !!template?.campaignEligible;
          const editable = !!template?.editableBlocks?.length;

          return (
            <TemplateCard key={key} $selected={selected === key}>
              <div
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
              >
                <Badge tone="indigo" caps>
                  {card.badge}
                </Badge>
                <span style={{ color: T.faint }}>
                  <Icons.Mail size={17} />
                </span>
              </div>

              <CardName>{card.name}</CardName>
              <Blurb>{card.blurb}</Blurb>

              <Divider />

              <div style={{ display: 'flex', gap: 9 }}>
                <Btn
                  icon="Pencil"
                  onClick={() => onSelect(key)}
                  style={{ flex: 1 }}
                  title={
                    editable
                      ? 'Open in the template editor'
                      : 'Open in the template editor — this one’s copy is fixed, so the fields are read-only'
                  }
                >
                  {editable ? 'Edit Template' : 'Open Template'}
                </Btn>
                <IconBtn
                  type="button"
                  onClick={() => copyKey(key)}
                  title={`Copy the template key "${key}"`}
                  aria-label={`Copy the template key ${key}`}
                >
                  {copied === key ? <Icons.Check size={16} /> : <Icons.Copy size={15} />}
                </IconBtn>
              </div>

              <div style={{ display: 'flex', gap: 16, justifyContent: 'center' }}>
                <LinkBtn type="button" onClick={() => onPreview?.(key, 'desktop')}>
                  <Icons.Monitor size={13} />
                  Desktop Preview
                </LinkBtn>
                <LinkBtn type="button" onClick={() => onPreview?.(key, 'mobile')}>
                  <Icons.Phone size={13} />
                  Mobile Preview
                </LinkBtn>
              </div>

              {/*
                The footer line, on every card rather than on eight of the nine.

                It was rendered only for the templates that *cannot* be composed, so the Campaign
                card — the one card an operator uses most — was a line shorter than its neighbours.
                In a stretch grid that does not make it shorter; it makes its blurb absorb the
                slack, so its divider, its buttons and its preview links all sit lower than the same
                controls on every card beside it. The card that looked wrong was the only one with
                nothing to say here.

                So it says the true thing instead. Both halves are worth stating on their own: one
                warns that a template cannot be aimed at an audience, and the other confirms that
                this one can — which, on the card that opens the builder, is the fact an operator is
                checking for.
              */}
              <Note>
                {composable
                  ? 'Can be sent to an audience — this is the campaign template.'
                  : 'Sent on an account event — not available as a campaign.'}
              </Note>
            </TemplateCard>
          );
        })}
      </CatalogGrid>

      {mismatched.length > 0 ? (
        <Note>
          Badge words are the design&apos;s. Under the hood {mismatched.length} of these sit in a
          different category —{' '}
          {mismatched.map((key) => `${CARDS[key].name} is ${byKey.get(key)?.category}`).join(', ')}{' '}
          — and it is the category, not the badge, that decides whether an unsubscribe silences the
          email.
        </Note>
      ) : null}
    </div>
  );
};

export default TemplateList;

/**
 * The display name for a template key, for screens outside this catalog.
 *
 * The template detail band and its breadcrumb both showed the raw key — `passwordReset` — two
 * clicks after the card that called it "Password Reset". Falling back to the key is deliberate: a
 * template added to the backend and not yet to `CARDS` should read as its key rather than blank.
 */
export const templateName = (key) => CARDS[key]?.name || key;
